import React, { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { toast } from 'react-toastify';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { documentsActionButtonSx, documentsContentSx } from 'components/Documents/documentsLayout';
import type { UploadWorkflowMode } from 'components/Documents/Scanner/types';
import { uploadPatientDocumentAPI } from 'services/documentServices';

import DocumentScannerWorkflow from './Scanner/DocumentScannerWorkflow';
import FileUploadWorkflow from './FileUploadWorkflow';
import theme from 'theme/index';

interface DocumentsContentContainerProps {
  patientId: string;
  onUploadSuccess?: () => void;
}

const DocumentsContentContainer: React.FC<DocumentsContentContainerProps> = ({
  patientId,
  onUploadSuccess,
}) => {
  const [mode, setMode] = useState<UploadWorkflowMode>('choice');
  
  const showScanButton =
  useMediaQuery(theme.breakpoints.down('md')) &&
  navigator.maxTouchPoints > 0;

  const handleScanUpload = useCallback(
    async (payload: {
      documentName: string;
      documentDescription?: string;
      generatedPdf: Blob;
      images: Blob[];
    }) => {
      await uploadPatientDocumentAPI({
        patientId,
        documentName: payload.documentName,
        documentDescription: payload.documentDescription,
        generatedPdf: payload.generatedPdf,
        images: payload.images,
      });
      toast('Scanned document uploaded successfully.', { type: 'success' });
      onUploadSuccess?.();
    },
    [onUploadSuccess, patientId]
  );

  const handleFileUpload = useCallback(
    async (payload: {
      documentName: string;
      documentDescription?: string;
      sourceFile: File;
    }) => {
      await uploadPatientDocumentAPI({
        patientId,
        documentName: payload.documentName,
        documentDescription: payload.documentDescription,
        sourceFile: payload.sourceFile,
      });
      toast('Document uploaded successfully.', { type: 'success' });
      onUploadSuccess?.();
    },
    [onUploadSuccess, patientId]
  );

  if (mode === 'scan') {
    return (
      <Stack sx={documentsContentSx} spacing={2}>
        <Typography variant='body2' color='text.secondary'>
          Position the document in frame, capture pages, then finish to add details.
        </Typography>
        <DocumentScannerWorkflow
          patientId={patientId}
          onUpload={handleScanUpload}
          onCancel={() => setMode('choice')}
        />
      </Stack>
    );
  }

  if (mode === 'upload') {
    return (
      <Stack sx={documentsContentSx} spacing={2}>
        <Typography variant='body2' color='text.secondary'>
          Select an image, PDF, or Word document from your device.
        </Typography>
        <FileUploadWorkflow
          patientId={patientId}
          onUpload={handleFileUpload}
          onCancel={() => setMode('choice')}
        />
      </Stack>
    );
  }

  return (
    <Stack sx={documentsContentSx} spacing={2} alignItems='center'>
      {showScanButton && (
      <Typography variant='body1' color='text.secondary' textAlign='left'>
        Scan or upload documents to securely share them with your MindWeal care team.
      </Typography>
      )}
      {!showScanButton && (
      <Typography variant='body1' color='text.secondary' textAlign='left'>
        Upload documents to securely share them with your MindWeal care team.
      </Typography>
      )}

      {!patientId && (
        <Alert severity='warning' sx={{ width: '100%' }}>
          Sign in to upload documents.
        </Alert>
      )}

{showScanButton && (
      <Button
        variant='contained'
        startIcon={<CameraAltIcon />}
        onClick={() => setMode('scan')}
        disabled={!patientId}
        sx={documentsActionButtonSx}
      >
        Scan New Document
      </Button>
      )}

      <Button
        variant='outlined'
        startIcon={<CloudUploadIcon />}
        onClick={() => setMode('upload')}
        disabled={!patientId}
        sx={documentsActionButtonSx}
      >
        Upload Existing Document
      </Button>
    </Stack>
  );
};

export default DocumentsContentContainer;
